import { useCallback, useEffect, useState } from 'react';

import { updateSettings } from '../lib/actions';
import { shortDateTime } from '../lib/format';
import { supabase } from '../lib/supabase';
import type { SocialMode } from '../lib/types';

/**
 * Auto post - the switch, the stop, and the caps (7 Oct 2026).
 *
 * The rest of the outbound side of this app is a queue a human empties. This section is the one
 * place that decides whether the *local poster* (`scripts/post-facebook.mjs`, running on the
 * operator's own PC against a session they signed into by hand) is allowed to empty it instead.
 *
 * Four things, and each is one line of the settings row the Worker already enforces:
 *
 *  - **Auto post** writes `mode_social`. On (`auto`) the poster is offered every reply the
 *    guardrails allow. Off (`assist_only`) it is offered only the replies queued with the Auto
 *    post button on a card - which is the safe half, and the one to test with.
 *  - **Stop everything** writes `kill_switch`, which `guardrails/policy.ts` reads before anything
 *    else. It stops the poster mid-run: the next `next` call comes back refused, with the reason.
 *  - **The caps** are what the guardrail counts a reply against - per day overall, per group per
 *    day, and the minimum gap inside one group. They are limits on the *machine* now, so they are
 *    editable from the machine's own control panel rather than being folklore.
 *  - **The last delivery** answers "has the poster run?" without opening a terminal.
 *
 * Deliberately not here: the mode columns for the forum and YouTube lanes. Nothing runs on those
 * lanes yet, and a switch that controls nothing is how the old controls section earned its delete.
 */

interface Props {
  actor: string;
}

interface LoadedSettings {
  kill_switch: boolean;
  mode_social: SocialMode;
  daily_cap_total: number;
  daily_cap_per_source: number;
  min_minutes_between_same_source: number;
}

interface LastDelivery {
  posted_at: string;
  posted_by: string | null;
  mode: string;
}

export default function AutoPostSection({ actor }: Props) {
  const [loaded, setLoaded] = useState<LoadedSettings | null>(null);
  const [last, setLast] = useState<LastDelivery | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** The cap boxes, as strings so a half-typed number is not rewritten while it is typed. */
  const [caps, setCaps] = useState({ total: '', perSource: '', gap: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: readError } = await supabase
      .from('settings')
      .select(
        'kill_switch, mode_social, daily_cap_total, daily_cap_per_source, min_minutes_between_same_source',
      )
      .eq('id', 1)
      .maybeSingle();

    if (readError) {
      setError(readError.message);
      setLoading(false);
      return;
    }

    if (data) {
      const settings = data as unknown as LoadedSettings;
      setLoaded(settings);
      setCaps({
        total: String(settings.daily_cap_total),
        perSource: String(settings.daily_cap_per_source),
        gap: String(settings.min_minutes_between_same_source),
      });
    }

    // Diagnostics, so a failure here must not take the controls down with it.
    const { data: delivery } = await supabase
      .from('deliveries')
      .select('posted_at, posted_by, mode')
      .order('posted_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    setLast((delivery as unknown as LastDelivery | null) ?? null);

    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function write(patch: Partial<LoadedSettings>, done: string) {
    setBusy(true);
    setStatus(null);
    setError(null);
    const result = await updateSettings(actor, patch);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'Could not save that.');
      await load();
      return;
    }
    setLoaded((current) => (current ? { ...current, ...patch } : current));
    setStatus(done);
  }

  const capsChanged =
    loaded !== null &&
    (caps.total !== String(loaded.daily_cap_total) ||
      caps.perSource !== String(loaded.daily_cap_per_source) ||
      caps.gap !== String(loaded.min_minutes_between_same_source));

  async function saveCaps() {
    const total = Number(caps.total);
    const perSource = Number(caps.perSource);
    const gap = Number(caps.gap);
    if (!Number.isInteger(total) || total < 0 || !Number.isInteger(perSource) || perSource < 0) {
      setError('The caps are whole numbers of replies, zero or more.');
      return;
    }
    if (!Number.isInteger(gap) || gap < 1) {
      setError('The gap is a whole number of minutes, one or more.');
      return;
    }
    await write(
      {
        daily_cap_total: total,
        daily_cap_per_source: perSource,
        min_minutes_between_same_source: gap,
      },
      'Caps saved. The guardrail counts every reply against them from now on.',
    );
  }

  const mode = loaded?.mode_social ?? 'off';
  const autoOn = mode === 'auto';

  return (
    <section className="setup-section">
      <h3>Auto post</h3>
      <p className="muted small">
        Replies are sent by the poster on your own PC (<code>npm run post:fb</code>), never by this
        dashboard and never by the cloud. It asks the Worker what may go, types exactly that into
        Facebook, and reports what happened; the guardrails below decide whether anything may go at
        all.
      </p>

      {status ? (
        <p className="notice" role="status">
          {status}
        </p>
      ) : null}
      {error ? <p className="error">{error}</p> : null}

      {loading ? <p className="muted">Loading the controls…</p> : null}

      {!loading && loaded ? (
        <>
          <label className="switch-row">
            <input
              type="checkbox"
              checked={autoOn}
              disabled={busy}
              onChange={(event) => {
                const next: SocialMode = event.target.checked ? 'auto' : 'assist_only';
                void write(
                  { mode_social: next },
                  next === 'auto'
                    ? 'Auto post is on: the poster sends every reply the guardrails allow.'
                    : 'Auto post is off: only replies you queue with Auto post on a card will be sent.',
                );
              }}
            />
            <span>
              <b>Auto post</b>
              <span className="muted small">
                {mode === 'auto'
                  ? ' — on: everything the guardrails allow is offered to the poster.'
                  : mode === 'assist_only'
                    ? ' — off: only replies you queue with Auto post on a card are offered.'
                    : ` — ${mode} at the source, so the poster is offered nothing; turning this on switches the lane to auto.`}
              </span>
            </span>
          </label>

          <label className="switch-row">
            <input
              type="checkbox"
              checked={loaded.kill_switch}
              disabled={busy}
              onChange={(event) =>
                void write(
                  { kill_switch: event.target.checked },
                  event.target.checked
                    ? 'Stopped. Nothing will be sent, by hand or by the poster, until this is off.'
                    : 'Running again.',
                )
              }
            />
            <span>
              <b>Stop everything</b>
              <span className="muted small">
                {' '}
                — the kill switch the guardrail reads first. It stops the poster mid-run: its next
                call comes back refused.
              </span>
            </span>
          </label>

          <div className="prompt-foot">
            <label className="muted small">
              Max per day{' '}
              <input
                type="number"
                min={0}
                step={1}
                value={caps.total}
                disabled={busy}
                onChange={(event) => setCaps((current) => ({ ...current, total: event.target.value }))}
                style={{ width: 72 }}
              />
            </label>
            <label className="muted small">
              Max per group per day{' '}
              <input
                type="number"
                min={0}
                step={1}
                value={caps.perSource}
                disabled={busy}
                onChange={(event) => setCaps((current) => ({ ...current, perSource: event.target.value }))}
                style={{ width: 72 }}
              />
            </label>
            <label className="muted small">
              Minutes between replies in one group{' '}
              <input
                type="number"
                min={1}
                step={1}
                value={caps.gap}
                disabled={busy}
                onChange={(event) => setCaps((current) => ({ ...current, gap: event.target.value }))}
                style={{ width: 72 }}
              />
            </label>
            <span className="grow" />
            <button type="button" disabled={busy || !capsChanged} onClick={() => void saveCaps()}>
              {busy ? 'Saving…' : 'Save caps'}
            </button>
          </div>

          <p className="muted small">
            {last
              ? `Last posted ${shortDateTime(last.posted_at)} by ${last.posted_by ?? 'unknown'} (${last.mode}).`
              : 'Nothing has been posted yet.'}{' '}
            Leave <code>npm run post:fb -- --watch</code> running to send queued replies within
            seconds.
          </p>
        </>
      ) : null}
    </section>
  );
}
